import {
  InChangeFilterParameters,
  SetGlobalSettingsRequest,
} from '@common-modules/mediaDetails/actions';
import {
  FetchEngineResultsRequest,
  InLoadMediaRequest,
  SaveDataRequest,
  SendToDownloadRequest,
  SendToHeadDetectionRequest,
  SendToRedactRequest,
  SetAudioEditsRequest,
  ApplyDeleteTranscriptionRequest,
  SetBackgroundBlurEditsRequest,
  SetTDORequest,
  PollDownloadAssetStop,
  OpticalTrackingJobInfo,
  AuthResponse,
} from '../models';
import { createGraphQLSuccessAction } from '@helpers/callGraphQLApi';
import { InLoadVoiceMaskAssetRequest, V3FJobResponse } from '../services';
import { createAction } from '@reduxjs/toolkit';
import { META_FLAG as WORKER_META_FLAG } from 'src/utils/workeredux/client/actions';

/**
 * Custom action creator that includes the worker flag at the top level.
 */
function createWorkerAction<P>(type: string) {
  const baseAction = createAction<P>(type);

  return Object.assign(
    (payload: P) =>
      Object.assign(baseAction(payload), { [WORKER_META_FLAG]: true }),
    baseAction // Preserve type inference from `createAction`
  );
}

/**
 * Incoming Action
 *
 * Marked as worker action in main thread:
 * - SET_GLOBAL_SETTINGS
 * - SET_SELECTED_GROUPS
 * - NEW_OVERLAY
 * - MAKE_TRACKING_PARENT_OVERLAY
 * - DELETE_OVERLAY
 * - DELETE_OVERLAY_GROUP
 * - CHANGE_OVERLAY
 * - UPDATE_CURRENT_TIME_MEDIA_PLAYER
 * - OpticalTracking.CREATE_ENGINE_JOB
 */

export const IN_SET_AUTH = createWorkerAction<AuthResponse>(
  'vtn-redact-web-worker/IN_SET_AUTH'
);

export const IN_SET_CONFIG = createWorkerAction<Window['config']>(
  'vtn-redact-web-worker/IN_SET_CONFIG'
);

export const IN_SET_GLOBAL_SETTINGS =
  createWorkerAction<SetGlobalSettingsRequest>(
    'vtn-redact-web-worker/IN_SET_GLOBAL_SETTINGS'
  );

export const IN_SET_TDO = createWorkerAction<SetTDORequest>(
  'vtn-redact-web-worker/IN_SET_TDO'
);

export const IN_SET_AUDIO_EDITS = createWorkerAction<SetAudioEditsRequest>(
  'vtn-redact-web-worker/IN_SET_AUDIO_EDITS'
);

export const IN_SET_BACKGROUND_BLUR_EDITS =
  createWorkerAction<SetBackgroundBlurEditsRequest>(
    'vtn-redact-web-worker/IN_SET_BACKGROUND_BLUR_EDITS'
  );

/**
 * Load ALL data for the TDO.
 */
export const IN_LOAD_MEDIA_DATA = createWorkerAction<InLoadMediaRequest>(
  'vtn-redact-web-worker/IN_LOAD_MEDIA_DATA'
);

export const IN_CLOSE_MEDIA = createWorkerAction<void>(
  'vtn-redact-web-worker/IN_CLOSE_MEDIA'
);

// export const IN_LOAD_MEDIA_DATA_SUCCESS =
//   'vtn-redact-web-worker/IN_LOAD_MEDIA_DATA_SUCCESS';
// export const actionInLoadDetectionDataSuccess = workerActionCreator<void>(
//   IN_LOAD_MEDIA_DATA_SUCCESS
// );
export const IN_CLEAR_MEDIA_DATA = createWorkerAction<void>(
  'vtn-redact-web-worker/IN_CLEAR_MEDIA_DATA'
);

/**
 * Load Head Detection. Good for when a engine completes.
 */
export const IN_LOAD_DETECTION_DATA =
  createWorkerAction<FetchEngineResultsRequest>(
    'vtn-redact-web-worker/IN_LOAD_DETECTION_DATA'
  );

/**
 * Load Head Detection. Good for when a engine completes.
 */
export const IN_SEND_TO_HEAD_DETECTION =
  createWorkerAction<SendToHeadDetectionRequest>(
    'vtn-redact-web-worker/IN_SEND_TO_HEAD_DETECTION'
  );

/**
 * Load other data than detection. Good for engine completes.
 */
export const IN_LOAD_UDRS_AUDIO_DATA =
  createWorkerAction<FetchEngineResultsRequest>(
    'vtn-redact-web-worker/IN_LOAD_UDRS_AUDIO_DATA'
  );

// export const IN_SAVE_UDRS_DATA = 'vtn-redact-web-worker/IN_SAVE_UDRS_DATA';
// export const actionInSaveUDRSData = workerActionCreator<SaveUDRsRequest>(
//   IN_SAVE_UDRS_DATA
// );

export const IN_LOAD_TRACKING_ENGINE_RESULTS = createWorkerAction<
  FetchEngineResultsRequest & { job: OpticalTrackingJobInfo }
>('vtn-redact-web-worker/IN_LOAD_TRACKING_ENGINE_RESULTS');

export const IN_LOAD_CORSIGHT_DATA =
  createWorkerAction<FetchEngineResultsRequest>(
    'vtn-redact-web-worker/IN_LOAD_CORSIGHT_DATA'
  );

export const IN_LOAD_TRANSCRIPTION_DATA =
  createWorkerAction<FetchEngineResultsRequest>(
    'vtn-redact-web-worker/IN_LOAD_TRANSCRIPTION_DATA'
  );

export const IN_LOAD_SPEAKER_SEPARATION_DATA =
  createWorkerAction<FetchEngineResultsRequest>(
    'vtn-redact-web-worker/IN_LOAD_SPEAKER_SEPARATION_DATA'
  );

/**
 * By CATEGORY, never by engine id.
 *
 * Both speaker-separation engines publish into "speaker detection"
 * (`a856c447-…`) — the Veritone one and every Speechmatics variant, per that
 * engine's profile.yaml. Passing `engines` switches the query to engineIds and
 * drops categoryId entirely (see fetchTranscriptionResults), so narrowing to
 * the configured engine hid results produced by the other one: switch engines,
 * or open a TDO separated before the switch, and the speaker view was empty
 * even though the results were there.
 */
export const buildSpeakerSeparationLoadRequest = ({
  tdoId,
  mediaDuration,
  speakerDetectionCategoryId,
}: {
  tdoId: FetchEngineResultsRequest['tdoId'];
  mediaDuration: number;
  speakerDetectionCategoryId: string;
}): FetchEngineResultsRequest => ({
  tdoId,
  mediaDuration,
  categoryId: speakerDetectionCategoryId,
});

export const IN_APPLY_DELETE_TRANSCRIPTION =
  createWorkerAction<ApplyDeleteTranscriptionRequest>(
    'vtn-redact-web-worker/IN_APPLY_DELETE_TRANSCRIPTION'
  );

export const IN_APPLY_DELETE_SPEAKER_SEPARATION = createWorkerAction<{
  metadata?: { description: string };
}>('vtn-redact-web-worker/IN_APPLY_DELETE_SPEAKER_SEPARATION');

export const IN_LOAD_VOICE_MASK_ASSET =
  createWorkerAction<InLoadVoiceMaskAssetRequest>(
    'vtn-redact-web-worker/IN_LOAD_VOICE_MASK_ASSET'
  );

// TODO: rename IN_SAVE_CHANGES → WORKER_SAVE_CHANGES
// "IN_" prefix is non-standard; this is the internal worker dispatch used only by
// watchSaveChangesHandle after all preconditions are verified. Pair rename with
// FETCH_UPDATE_ASSET_ACTION → INITIATE_SAVE in mediaDetails/actions/actions.ts
export const IN_SAVE_CHANGES = createWorkerAction<SaveDataRequest>(
  'vtn-redact-web-worker/IN_SAVE_CHANGES'
);

export const IN_REDACT_MEDIA = createWorkerAction<SendToRedactRequest>(
  'vtn-redact-web-worker/IN_REDACT_MEDIA'
);

export const IN_DOWNLOAD_ASSET = createWorkerAction<SendToDownloadRequest>(
  'vtn-redact-web-worker/IN_DOWNLOAD_ASSET'
);

export const POLL_DOWNLOAD_ASSET_STOP =
  createWorkerAction<PollDownloadAssetStop>(
    'vtn-redact-web-worker/POLL_DOWNLOAD_ASSET_STOP'
  );

export const FETCH_DOWNLOAD_ASSET_SUCCESS = createGraphQLSuccessAction<
  V3FJobResponse,
  {
    readonly engineId: string;
    readonly id: string;
    readonly payload: {
      readonly exportAssetType: string;
      readonly mode: string;
      readonly user: string;
    };
    readonly tdoName: string;
    readonly _internalRequestId: string;
    readonly _shouldTrackRequestsIndividually: boolean;
  },
  true
>('vtn-redact-web-worker/FETCH_DOWNLOAD_ASSET_SUCCESS');

export const IN_CHANGE_FILTER_PARAMETERS =
  createWorkerAction<InChangeFilterParameters>(
    'vtn-redact-web-worker/IN_CHANGE_FILTER_PARAMETERS'
  );
